import type { TimelineEntry } from '../api/types.ts';
import { formatDate } from '../dates.ts';

function fmt(date: string | null): string {
  if (!date) return '—';
  return formatDate(date);
}

function duration(days: number | null): string {
  if (days === null) return '';
  if (days < 60) return `${days} days`;
  const months = Math.round(days / 30.4);
  if (months < 24) return `${months} months`;
  return `${(days / 365.25).toFixed(1)} years`;
}

/**
 * Where this client has been, newest first.
 *
 * Placements and temporary stays in one sequence, because that is how someone
 * reads a history: lived at Ozanam Hall from March, at Kings County Hospital
 * for eleven days in June with the bed held, back at Ozanam Hall since. Two
 * panels would make the reader interleave the dates themselves, which is the
 * work this screen exists to do for them.
 *
 * **A stay is an interruption, not a move**, and it has to look like one. It is
 * inset, hairline-ruled, and says where they still lived — the placement did
 * not end, the bed was held, the mail still went to the facility. Flattening
 * the two into identical rows would say the client moved to hospital and then
 * moved back, which is the reading the schema keeps three tables apart to
 * prevent.
 *
 * Visits appear here too, as events rather than periods — a visit that is
 * recorded and then shows nowhere reads as a failed save on exactly the
 * connection this was built for.
 *
 * Resolved stays matter most here. An open one already shows in the header;
 * a five-day admission that ended is the thing seven years of spreadsheets
 * could not record, and until this view existed the system recorded it and
 * then showed it nowhere.
 */
export function PlacementTimeline({ entries }: { entries: TimelineEntry[] }) {
  if (entries.length === 0) {
    return (
      <p className="px-4 py-8 text-center text-neutral-500">
        No placement history recorded.
      </p>
    );
  }

  return (
    <ol className="divide-y divide-neutral-200">
      {entries.map((e) =>
        e.entry_kind === 'Visit' ? (
          /*
            An event, not a period. One date, no range, no duration in days —
            a rule down the left edge rather than a block, so the eye reads the
            placements as the structure and the visits as marks against it.

            `lives_at` is set only when the visit happened somewhere other than
            where the client lived, so the line below only appears when there
            is something to say. "Visited at Crestwood Green · they live at
            Pembroke Point" is the sentence a court report needs; at their own
            placement there is nothing to add and saying it would be noise.
          */
          <li key={e.entry_id} className="py-3 pr-4 pl-8">
            <div className="border-l-2 border-neutral-900/25 pl-3">
              <div className="flex items-baseline gap-2">
                <p className="text-xs font-bold tracking-[0.12em] text-neutral-600 uppercase">
                  {e.kind_label}
                </p>
                <p className="ml-auto shrink-0 text-sm text-neutral-500 tabular-nums">
                  {fmt(e.started_on)}
                  {e.minutes !== null && ` · ${e.minutes} min`}
                </p>
              </div>

              <p className="mt-0.5 text-[0.9375rem] text-neutral-800">
                {e.location ? (
                  <>Visited at <span className="font-semibold">{e.location}</span></>
                ) : (
                  'Visited'
                )}
                {e.lives_at && (
                  <span className="text-neutral-600"> · they live at {e.lives_at}</span>
                )}
              </p>

              {e.outcome && (
                <p className="mt-1 inline-block rounded bg-warning-bg px-2 py-0.5 text-sm
                              font-semibold text-warning-fg">
                  {e.outcome}
                </p>
              )}

              {e.reason && (
                <p className="mt-1 line-clamp-3 text-sm leading-snug text-neutral-600">
                  {e.reason}
                </p>
              )}

              {e.recorded_by && (
                <p className="mt-1 text-xs text-neutral-500">{e.recorded_by}</p>
              )}
            </div>
          </li>
        ) : e.entry_kind === 'Stay' ? (
          <li key={e.entry_id} className="bg-neutral-50 py-3 pr-4 pl-8">
            <div className="border-l-2 border-neutral-300 pl-3">
              <p className="text-xs font-bold tracking-[0.12em] text-neutral-500 uppercase">
                Away · {e.kind_label}
                {e.is_current && ' · now'}
              </p>

              <p className="mt-0.5 font-semibold text-neutral-900">
                {e.location ?? 'Location not recorded'}
              </p>

              <p className="mt-1 text-sm text-neutral-600 tabular-nums">
                {fmt(e.started_on)} – {e.ended_on ? fmt(e.ended_on) : 'present'}
                {e.days !== null && <> · {duration(e.days)}</>}
              </p>

              {/* The placement did not end. Say so on the row itself, so the
                  reader never has to scan for what they went back to. */}
              {e.lives_at && (
                <p className="mt-1 text-sm text-neutral-600">
                  {e.bed_held ? 'Bed held at' : 'Lived at'}{' '}
                  <span className="font-medium text-neutral-800">{e.lives_at}</span>
                </p>
              )}

              {e.reason && (
                <p className="mt-1 text-sm text-neutral-600">Reason: {e.reason}</p>
              )}

              {e.outcome && e.outcome !== 'Returned' && (
                <p className="mt-1 text-sm font-medium text-neutral-700">{e.outcome}</p>
              )}
            </div>
          </li>
        ) : (
          <li key={e.entry_id} className="px-4 py-3.5">
            <div className="flex items-baseline gap-2">
              <p className="min-w-0 font-semibold text-neutral-900">
                {e.location ?? 'Location not recorded'}
              </p>
              {e.is_current && (
                <span className="ml-auto shrink-0 rounded-full bg-neutral-900 px-2.5 py-0.5
                                 text-xs font-bold tracking-wide text-white uppercase">
                  Current
                </span>
              )}
            </div>

            <p className="mt-0.5 text-[0.9375rem] text-neutral-600">
              {[e.kind_label, e.city].filter(Boolean).join(' · ')}
            </p>

            <p className="mt-1 text-sm text-neutral-500 tabular-nums">
              {fmt(e.started_on)} – {e.ended_on ? fmt(e.ended_on) : 'present'}
              {e.days !== null && <> · {duration(e.days)}</>}
            </p>

            {e.reason && (
              <p className="mt-1 text-sm text-neutral-600">Reason: {e.reason}</p>
            )}
          </li>
        ),
      )}
    </ol>
  );
}
